import ReactQuill from "react-quill";
import { EmailRecipient, EmailVariable } from "./types";

const Quill = ReactQuill.Quill;
const Embed = Quill.import("blots/embed");

interface VariableValue {
  token: string;
  label: string;
}

class VariableBlot extends Embed {
  static blotName = "variable";
  static tagName = "span";
  static className = "ql-variable";

  static create(value: VariableValue) {
    const node = super.create() as HTMLElement;
    node.setAttribute("data-token", value.token);
    node.setAttribute("data-label", value.label);
    node.textContent = value.label;
    return node;
  }

  static value(node: HTMLElement): VariableValue {
    return {
      token: node.getAttribute("data-token") ?? "",
      label: node.getAttribute("data-label") ?? node.textContent ?? "",
    };
  }
}

let registered = false;
export function registerVariableBlot() {
  if (registered) return;
  Quill.register(VariableBlot, true);
  registered = true;
}

const TOKEN_REGEX = /\{\{\s*([\w.]+)\s*\}\}/g;

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const pill = (variable: EmailVariable) =>
  `<span class="ql-variable" data-token="${escapeHtml(variable.token)}" data-label="${escapeHtml(variable.label)}">${escapeHtml(variable.label)}</span>`;

const withPills = (html: string, variables: EmailVariable[]) =>
  html.replace(TOKEN_REGEX, (match, token: string) => {
    const variable = variables.find((v) => v.token === token);
    return variable ? pill(variable) : match;
  });

export const bodyToEditorHtml = (template: string, variables: EmailVariable[]) =>
  withPills(template, variables);

export const subjectToEditorHtml = (
  template: string,
  variables: EmailVariable[],
) => `<p>${withPills(escapeHtml(template), variables)}</p>`;

const replacePills = (html: string) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll(".ql-variable").forEach((node) => {
    const token = node.getAttribute("data-token");
    node.replaceWith(doc.createTextNode(token ? `{{${token}}}` : ""));
  });
  return doc.body;
};

export const editorHtmlToBody = (html: string) => {
  const body = replacePills(html);
  if (!body.textContent?.replace(/﻿/g, "").trim() && !body.querySelector("img")) {
    return "";
  }
  return body.innerHTML.replace(/﻿/g, "");
};

export const editorHtmlToSubject = (html: string) =>
  (replacePills(html).textContent ?? "")
    .replace(/﻿/g, "")
    .replace(/\s+/g, " ")
    .trim();

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isValidEmail = (email: string) => EMAIL_REGEX.test(email);

export const isRecipientInvalid = (recipient: EmailRecipient) =>
  !!recipient.rejeitado || !isValidEmail(recipient.email);

export const byteSize = (text: string) => new Blob([text]).size;

let keySeed = 0;
export const newRecipientKey = () => `r${Date.now()}_${keySeed++}`;
